export type AppRole =
    | 'manager-admin'
    | 'moderator'
    | 'moderator-admin'
    | 'expert'
    | 'expert-admin'
    | 'expert-outstaff'
    | 'expert-admin-outstaff'
    | 'developer'
    | string;

export type AppPage =
    | 'reviews'
    | 'adsModeration'
    | 'complaints'
    | 'adsExpertModeration'
    | 'orders'
    | 'users'
    | 'customReasons'
    | 'promoCodes'
    | 'remoteConfig'
    | 'projectSettings'
    | 'aiModerationSettings'
    | 'adminUsers'
    | 'expertTariffs'
    | 'expertSla'
    | 'expertStatistics'
    | 'expertPersonalStatistics'
    | 'moderatorPersonalStatistics'
    | 'moderatorStatistics'
    | 'sellerDeficits'
    | 'userAdInfo';

const ALL = 'all' as const;
type PageAccess = typeof ALL | AppPage;
const EXPERT_MODERATION_READ_ONLY_ROLES = new Set<AppRole>(['moderator', 'moderator-admin']);

// Явные ограничения на страницу. Если страницы тут нет — по умолчанию доступ не ограничен.
const PAGE_ALLOWED_ROLES: Partial<Record<AppPage, AppRole[]>> = {
    adminUsers: ['manager-admin'],
    promoCodes: ['manager-admin', 'moderator-admin'],
    remoteConfig: ['manager-admin', 'developer', 'moderator-admin', 'moderator'],
    projectSettings: ['manager-admin'],
    aiModerationSettings: ['manager-admin'],
    expertTariffs: ['manager-admin'],
    expertSla: ['manager-admin'],
    expertStatistics: ['expert-admin-outstaff', 'manager-admin'],
    expertPersonalStatistics: ['manager-admin', 'expert-admin-outstaff', 'expert-outstaff', 'expert-admin', 'expert'],
    moderatorPersonalStatistics: ['moderator', 'moderator-admin', 'manager-admin'],
    moderatorStatistics: ['moderator-admin', 'manager-admin'],
    sellerDeficits: ['manager-admin']
};

// Ограничения по ролям. Роли, которых нет в этом объекте, получают доступ ко всем страницам по умолчанию.
const ROLE_PAGE_ALLOWLIST: Partial<Record<AppRole, PageAccess[]>> = {
    moderator: ['reviews', 'adsModeration', 'adsExpertModeration', 'complaints', 'orders', 'users', 'userAdInfo', 'moderatorPersonalStatistics', 'remoteConfig'],
    'moderator-admin': ['reviews', 'adsModeration', 'adsExpertModeration', 'complaints', 'orders', 'users', 'customReasons', 'promoCodes', 'userAdInfo', 'moderatorPersonalStatistics', 'moderatorStatistics', 'remoteConfig'],
    expert: ['adsExpertModeration', 'expertPersonalStatistics', 'users', 'userAdInfo'],
    'expert-admin': ['adsExpertModeration', 'expertPersonalStatistics', 'users', 'userAdInfo'],
    'expert-outstaff': ['adsExpertModeration', 'expertPersonalStatistics', 'users', 'userAdInfo'],
    'expert-admin-outstaff': ['adsExpertModeration', 'expertStatistics', 'expertPersonalStatistics', 'users', 'userAdInfo'],
    'manager-admin': [ALL]
};

export function canAccessPage(role: AppRole | undefined | null, page: AppPage): boolean {
    if (!role) {
        return false;
    }

    const pageAllowedRoles = PAGE_ALLOWED_ROLES[page];
    if (pageAllowedRoles && !pageAllowedRoles.includes(role)) {
        return false;
    }

    const roleAllowlist = ROLE_PAGE_ALLOWLIST[role];
    if (!roleAllowlist) {
        return true;
    }

    return roleAllowlist.includes(ALL) || roleAllowlist.includes(page);
}

export function canAccessExpertModerationDetails(role: AppRole | undefined | null): boolean {
    return canAccessPage(role, 'adsExpertModeration')
        && !EXPERT_MODERATION_READ_ONLY_ROLES.has(role as AppRole);
}
